/**
 * Keeps the HubSpot chat widget clear of the footer's bottom row (logo, legal
 * links, Site Credit).
 *
 * HubSpot pins its widget container to the bottom corner of the viewport and
 * its settings only offer left/right placement — no vertical offset. Once the
 * page is scrolled to the footer, that container (larger than the visible
 * launcher, and mostly transparent) sits on top of whatever links share its
 * corner and swallows their clicks. This element finds the highest link or
 * button in the row that sits in the widget's horizontal span, publishes how
 * far it has risen into the viewport as `--at-chat-widget-offset` on the
 * root, and at-footer.css lifts the widget by that amount. Only links under
 * the widget count: on mobile the legal links are left-aligned beside it, so
 * the widget only needs to clear the full-width logo and stays low instead
 * of covering the accordion above.
 *
 * When the widget does have to clear the legal links (narrow viewports where
 * its container reaches them, or 990px+ where they sit under it), the row may
 * not have enough height between those links and the accordion above, and
 * the lifted container would cover the accordion's last row. In that case the
 * missing height is published as `--at-chat-widget-reserve` on this element
 * and added above the row, so the widget sits in that space instead.
 *
 * Measurements use the launcher's footprint, anchored to the container's
 * bottom corner, rather than the container's current size. HubSpot resizes
 * the container for each state: its launchers (the round button, the "Ask me
 * anything" pill) are never taller than wide, while its popups (the greeting
 * bubble, the open chat) are. A popup keeps the last launcher footprint, so
 * it overlays the page like it does everywhere else instead of pushing the
 * widget into the accordion or reflowing the footer.
 */
class FooterBottom extends HTMLElement {
  #controller = new AbortController();
  #resizeObserver = new ResizeObserver(() => this.#schedule());
  /** @type {HTMLElement | null} */
  #widget = null;
  /** @type {HTMLElement | null} */
  #above = null;
  /** @type {HTMLElement[]} */
  #targets = [];
  /** @type {number | null} */
  #rafId = null;
  #gap = 0;
  #offset = 0;
  #reserve = 0;
  #launcherWidth = 0;
  #launcherHeight = 0;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#targets = [...this.querySelectorAll('a, button')];
    this.#above = this.parentElement?.querySelector(':scope > .at-footer__row') ?? null;
    this.#gap = parseFloat(getComputedStyle(this).getPropertyValue('--at-chat-widget-gap')) || 0;

    // Capture phase: on desktop the theme scrolls `.page-wrapper` instead of
    // the document (see scroll-container.js), and element scroll events don't
    // bubble — capturing on `document` covers both scroll containers.
    document.addEventListener('scroll', this.#schedule, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal });

    this.#update();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    this.#setOffset(0);
    this.#setReserve(0);
  }

  #schedule = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update() {
    const widget = document.getElementById('hubspot-messages-iframe-container');
    if (!widget) {
      this.#setOffset(0);
      this.#setReserve(0);
      return;
    }

    // HubSpot resizes its container between its launchers, the greeting bubble
    // and the open chat — re-measure on each change.
    if (widget !== this.#widget) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver.observe(widget);
      this.#widget = widget;
      this.#launcherWidth = 0;
      this.#launcherHeight = 0;
    }

    const rect = widget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    if (rect.height <= rect.width || !this.#launcherWidth) {
      this.#launcherWidth = rect.width;
      this.#launcherHeight = rect.height;
    }

    const alignLeft = widget.classList.contains('widget-align-left');
    const left = alignLeft ? rect.left : rect.right - this.#launcherWidth;
    const right = alignLeft ? rect.left + this.#launcherWidth : rect.right;
    let top = Infinity;

    for (const target of this.#targets) {
      const targetRect = target.getBoundingClientRect();
      if (!targetRect.width || targetRect.right <= left || targetRect.left >= right) continue;
      top = Math.min(top, targetRect.top);
    }

    if (top === Infinity) {
      this.#setOffset(0);
      this.#setReserve(0);
      return;
    }

    this.#setOffset(Math.max(0, Math.round(window.innerHeight - top + this.#gap)));

    // Height available between the accordion and the highest link under the
    // widget once the current reserve is taken out — independent of scroll.
    if (this.#above) {
      const available = top - this.#above.getBoundingClientRect().bottom - this.#reserve;
      this.#setReserve(Math.max(0, Math.ceil(this.#launcherHeight + this.#gap - available)));
    }
  }

  /** @param {number} offset */
  #setOffset(offset) {
    if (offset === this.#offset) return;
    this.#offset = offset;

    const root = document.documentElement;
    root.classList.toggle('at-chat-widget-lifted', offset > 0);
    root.style.setProperty('--at-chat-widget-offset', `${offset}px`);
  }

  /** @param {number} reserve */
  #setReserve(reserve) {
    if (reserve === this.#reserve) return;
    this.#reserve = reserve;

    this.style.setProperty('--at-chat-widget-reserve', `${reserve}px`);
    // The reserve moves the links — measure the lift again against them.
    if (this.isConnected) this.#schedule();
  }
}

if (!customElements.get('at-footer-bottom')) {
  customElements.define('at-footer-bottom', FooterBottom);
}
